import { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import {
  useResourcePlanner,
  useCreateResourceBooking,
  useDeleteResourceBooking,
  type CreateBookingPayload,
} from './hooks/useCapacity';
import { JobSearchModal, type JobSearchResult } from '@/components/JobSearchModal';
import { getErrorMessage } from '@/lib/query-client';

// ─── Helpers ────────────────────────────────────────────────────────────────

function getWeekStarts(startDate: string, endDate: string): string[] {
  const weeks: string[] = [];
  const [sy, sm, sd] = startDate.split('-').map(Number);
  const current = new Date(sy, sm - 1, sd);
  const end = new Date(...(endDate.split('-').map(Number) as [number, number, number]));
  end.setMonth(end.getMonth() - 1); // fix month offset

  // Align to Monday
  const day = current.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  current.setDate(current.getDate() + diff);

  const endObj = new Date(
    endDate.split('-').map(Number)[0],
    endDate.split('-').map(Number)[1] - 1,
    endDate.split('-').map(Number)[2],
  );

  while (current <= endObj) {
    const y = current.getFullYear();
    const m = String(current.getMonth() + 1).padStart(2, '0');
    const d = String(current.getDate()).padStart(2, '0');
    weeks.push(`${y}-${m}-${d}`);
    current.setDate(current.getDate() + 7);
  }

  return weeks;
}

function formatWeekLabel(weekStart: string): string {
  const [y, m, d] = weekStart.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ─── Component ──────────────────────────────────────────────────────────────

export function WeeklyResourcePlanner({ teamId, weeks }: { teamId?: string; weeks: number }) {
  const { toast } = useToast();

  // Calculate date range
  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    const day = now.getDay();
    const mondayDiff = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(monday.getDate() + mondayDiff);

    const endObj = new Date(monday);
    endObj.setDate(endObj.getDate() + weeks * 7 - 1);

    const fmt = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    return { startDate: fmt(monday), endDate: fmt(endObj) };
  }, [weeks]);

  const weekStarts = useMemo(() => getWeekStarts(startDate, endDate), [startDate, endDate]);

  const { data: plannerData, isLoading } = useResourcePlanner({ startDate, endDate, teamId });
  const createBooking = useCreateResourceBooking();
  const deleteBooking = useDeleteResourceBooking();

  // Add booking modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addModalData, setAddModalData] = useState<{ userId: string; weekStart: string } | null>(
    null,
  );
  const [addJobId, setAddJobId] = useState('');
  const [addJobLabel, setAddJobLabel] = useState('');
  const [addHours, setAddHours] = useState('');
  const [showJobSearch, setShowJobSearch] = useState(false);

  const handleCellClick = (userId: string, weekStart: string) => {
    setAddModalData({ userId, weekStart });
    setAddJobId('');
    setAddJobLabel('');
    setAddHours('');
    setShowAddModal(true);
  };

  const handleJobSelected = (job: JobSearchResult) => {
    setAddJobId(job.id);
    setAddJobLabel(`${job.title} – ${job.clientName}`);
    setShowJobSearch(false);
  };

  const handleCreateBooking = () => {
    if (!addModalData || !addJobId || !addHours) return;

    const payload: CreateBookingPayload = {
      user_id: addModalData.userId,
      job_id: addJobId,
      week_start: addModalData.weekStart,
      hours: Number(addHours),
    };

    createBooking.mutate(payload, {
      onSuccess: () => {
        toast({ variant: 'success', title: 'Booking created.' });
        setShowAddModal(false);
      },
      onError: (error) => {
        toast({
          variant: 'error',
          title: 'Failed to create booking.',
          description: getErrorMessage(error),
        });
      },
    });
  };

  const handleDeleteBooking = (bookingId: string) => {
    deleteBooking.mutate(bookingId, {
      onSuccess: () => toast({ variant: 'success', title: 'Booking removed.' }),
      onError: (error) =>
        toast({
          variant: 'error',
          title: 'Failed to remove booking.',
          description: getErrorMessage(error),
        }),
    });
  };

  return (
    <div className="space-y-4">
      {/* Resource Grid */}
      <Card>
        <CardHeader
          title="Weekly Resource Planner"
          description="Click a cell to book hours for a staff member"
        />
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full text-sm border-collapse"
                aria-label="Weekly resource planner"
              >
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 min-w-[150px]">
                      Staff
                    </th>
                    {weekStarts.map((ws) => (
                      <th
                        key={ws}
                        className="border border-gray-200 dark:border-gray-700 px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 min-w-[120px] bg-gray-50 dark:bg-gray-800"
                      >
                        {formatWeekLabel(ws)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(plannerData?.staff ?? []).map((staff) => (
                    <tr key={staff.userId}>
                      <td className="sticky left-0 z-10 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-3 py-2">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {staff.userName}
                        </div>
                      </td>
                      {weekStarts.map((ws) => {
                        const cellBookings = staff.bookings.filter((b) => b.weekStart === ws);
                        const totalHours = cellBookings.reduce((s, b) => s + b.hours, 0);

                        return (
                          <td
                            key={ws}
                            className="border border-gray-200 dark:border-gray-700 px-1 py-1 align-top cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-950/20 transition-colors min-h-[60px]"
                            onClick={() => handleCellClick(staff.userId, ws)}
                          >
                            {cellBookings.length > 0 ? (
                              <div className="space-y-1">
                                {cellBookings.map((b) => (
                                  <div
                                    key={b.id}
                                    className="bg-blue-100 dark:bg-blue-900/30 rounded px-1.5 py-0.5 text-xs group relative"
                                  >
                                    <div className="font-medium text-blue-800 dark:text-blue-300 truncate">
                                      {b.jobTitle || 'Job'}
                                    </div>
                                    <div className="text-blue-600 dark:text-blue-400">
                                      {b.hours}h
                                    </div>
                                    {b.notes && (
                                      <div className="text-[10px] text-blue-500 dark:text-blue-400 truncate italic">
                                        {b.notes}
                                      </div>
                                    )}
                                    <button
                                      type="button"
                                      className="absolute top-0 right-0 hidden group-hover:block text-red-500 hover:text-red-700 p-0.5"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteBooking(b.id);
                                      }}
                                      aria-label="Remove booking"
                                    >
                                      <svg
                                        className="w-3 h-3"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={2}
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M6 18L18 6M6 6l12 12"
                                        />
                                      </svg>
                                    </button>
                                  </div>
                                ))}
                                <div className="text-[10px] text-gray-500 text-center">
                                  {totalHours}h total
                                </div>
                              </div>
                            ) : (
                              <div className="h-10 flex items-center justify-center text-gray-300 dark:text-gray-700 text-xs">
                                +
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {(plannerData?.staff ?? []).length === 0 && (
                    <tr>
                      <td
                        colSpan={weekStarts.length + 1}
                        className="border border-gray-200 dark:border-gray-700 px-4 py-8 text-center text-sm text-gray-500"
                      >
                        No staff members found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Booking Modal */}
      {showAddModal && addModalData && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setShowAddModal(false)}
        >
          <div className="card w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Book Hours
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Week of {formatWeekLabel(addModalData.weekStart)}
            </p>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Job
                </label>
                <button
                  type="button"
                  onClick={() => setShowJobSearch(true)}
                  className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm shadow-sm hover:border-brand-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:border-brand-400"
                >
                  <span
                    className={addJobLabel ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}
                  >
                    {addJobLabel || 'Search & select a job...'}
                  </span>
                  <svg
                    className="h-4 w-4 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </button>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Hours
                </label>
                <input
                  type="number"
                  className="input-field w-full"
                  value={addHours}
                  onChange={(e) => setAddHours(e.target.value)}
                  min="0.5"
                  max="60"
                  step="0.5"
                  placeholder="e.g. 8"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateBooking}
                disabled={!addJobId || !addHours || createBooking.isPending}
              >
                {createBooking.isPending ? 'Booking...' : 'Book'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Job Search Modal */}
      {showJobSearch && (
        <JobSearchModal
          onSelect={handleJobSelected}
          onClose={() => setShowJobSearch(false)}
          title="Select Job for Booking"
          defaultStatus=""
        />
      )}
    </div>
  );
}
